import { Injectable } from '@angular/core';

import { createEffect, Actions, ofType } from '@ngrx/effects';
import * as fromAccountAction from '@jepto/features/settings/store/account/actions/account.action';

import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import { of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
	sentryLogEvent,
	sentryLogError,
	sentryLogSuccess,
} from '@jepto/shared/utils/collection';
import { environment } from '@jepto/environments/environment';

import mutationAccountUpload from '@jepto/features/settings/graphql/account/mutations/mutation-accountUpload';
import updateAccount from '@jepto/features/settings/graphql/account/mutations/mutation-updateAccount';
import { normalizeIanaTimezone } from '@framework/utils/iana-timezone-location-list';

@Injectable()
export class AccountEffects {
	constructor(
		private actions$: Actions,
		private requestService: JepRequestService
	) {}

	updateDetails$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromAccountAction.AccountActionType.UpdateDetails),
				map((action: any) => action.payload),
				switchMap((payload: any) => {
					const normalizedPayload = {
						...payload,
						timezone: normalizeIanaTimezone(payload.timezone),
					};
					sentryLogEvent(
						'Update Account Details',
						normalizedPayload
					);
					return this.requestService
						.mutate({
							mutation: updateAccount,
							variables: { input: normalizedPayload },
						})
						.pipe(
							take(1),
							switchMap((result: any) => {
								if (
									!result.data['editAccount'] &&
									result.errors &&
									result.errors[0] &&
									result.errors[0].errorType === '404' &&
									result.errors[0].message
								) {
									sentryLogError(
										JSON.stringify(result)
									);
									return of(
										new fromAccountAction.TransactionError({
											data: null,
											success: false,
											error: true,
											transacting: false,
											isUpdatingAccountLogo: false,
											isUpdatingAccountLogoError: false,
											isAccountLogoUpdated: false,
										})
									);
								} else {
									sentryLogSuccess(
										JSON.stringify(result)
									);
									return of(
										new fromAccountAction.TransactionSuccess(
											{
												data: result.data[
													'editAccount'
												],
												success: true,
												error: false,
												transacting: false,
												isUpdatingAccountLogo: false,
												isUpdatingAccountLogoError: false,
												isAccountLogoUpdated: false,
											}
										)
									);
								}
							})
						);
				})
			),
		{ dispatch: true }
	);

	updateLogo$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromAccountAction.AccountActionType.UpdateLogo),
				map((action: any) => action.payload),
				switchMap((payload: any) => {
					sentryLogEvent(
						'Update Account Logo',
						payload
					);
					return this.requestService
						.mutate({
							mutation: mutationAccountUpload,
							variables: { input: payload },
						})
						.pipe(
							take(1),
							switchMap((result: any) => {
								if (result?.errors && result.errors[0]) {
									sentryLogError(
										JSON.stringify(result)
									);
									return of(
										new fromAccountAction.TransactionError({
											success: false,
											error: true,
											transacting: false,
											isUpdatingAccountLogo: false,
											isUpdatingAccountLogoError: true,
											isAccountLogoUpdated: false,
										})
									);
								} else {
									sentryLogSuccess(
										JSON.stringify(result)
									);
									return of(
										new fromAccountAction.TransactionSuccess(
											{
												success: false,
												error: false,
												transacting: false,
												isUpdatingAccountLogo: false,
												isUpdatingAccountLogoError: false,
												isAccountLogoUpdated: true,
											}
										)
									);
								}
							})
						);
				})
			),
		{ dispatch: true }
	);
}
